import { Filter } from "lucide-react"
import { useTranslation } from "react-i18next"

import { DebouncedSearchInput } from "@/components/shared/debounced-search-input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { useIsMobile } from "@/hooks/use-mobile"
import type {
  MissionListDeadlineWindow,
  MissionListFilterOptions,
  MissionListFilters,
  MissionListMissionType,
  MissionListStatus,
} from "@/types/missions"

/**
 * OKS-31 — Brand Mission list filter rail.
 *
 * Desktop ≥768px: inline row — Search · Status · Type · Deadline window
 * · Clear all (Clear renders only when ≥1 filter is non-default).
 *
 * Mobile <768px: collapses behind a "Filters" button (matches OKS-25
 * `crew-filter-rail` mobile pattern) that opens a `Sheet` with the same
 * filters stacked vertically. Search stays above the Sheet trigger so
 * it's always one tap.
 *
 * Form-shaped inputs carry literal `name` attributes matching the
 * backend strong-params keys (`status`, `mission_type`, `deadline_window`,
 * `q`) — Vitest URL canary asserts the wire format.
 */
interface MissionListFilterRailProps {
  filters: MissionListFilters
  filterOptions: MissionListFilterOptions
  onStatusChange: (value: MissionListStatus) => void
  onMissionTypeChange: (value: MissionListMissionType) => void
  onDeadlineWindowChange: (value: MissionListDeadlineWindow) => void
  onSearchChange: (q: string | null) => void
  onClearAll: () => void
}

export function MissionListFilterRail(props: MissionListFilterRailProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const showClear = hasActiveFilter(props.filters)

  const fields = (
    <FilterFields
      filters={props.filters}
      filterOptions={props.filterOptions}
      onStatusChange={props.onStatusChange}
      onMissionTypeChange={props.onMissionTypeChange}
      onDeadlineWindowChange={props.onDeadlineWindowChange}
    />
  )

  if (isMobile) {
    return (
      <div
        data-testid="mission-list-filter-bar"
        className="flex flex-col gap-2"
      >
        <DebouncedSearchInput
          defaultValue={props.filters.q ?? ""}
          onDebouncedChange={(v) => props.onSearchChange(v ? v : null)}
          testId="mission-list-search"
          ariaLabel={t("missions.index.filters.search_aria")}
          placeholder={t("missions.index.filters.search_placeholder")}
          name="q"
        />
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  data-testid="mission-list-filter-button"
                >
                  <Filter className="mr-1 size-4" aria-hidden="true" />
                  {t("missions.index.filters.mobile_button_label")}
                </Button>
              }
            />
            <SheetContent
              side="right"
              data-testid="mission-list-filter-mobile-sheet"
            >
              <SheetHeader>
                <SheetTitle>
                  {t("missions.index.filters.mobile_sheet_title")}
                </SheetTitle>
                <SheetDescription className="sr-only">
                  {t("missions.index.filters.mobile_sheet_title")}
                </SheetDescription>
              </SheetHeader>
              <div className="flex flex-col gap-4 p-4">{fields}</div>
              <SheetFooter className="[&:empty]:hidden">
                {showClear && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={props.onClearAll}
                    data-testid="mission-list-filter-clear-mobile"
                  >
                    {t("missions.index.filters.clear")}
                  </Button>
                )}
              </SheetFooter>
            </SheetContent>
          </Sheet>
          {showClear && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={props.onClearAll}
              data-testid="mission-list-filter-clear"
            >
              {t("missions.index.filters.clear")}
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="mission-list-filter-bar"
      className="flex flex-wrap items-center gap-2"
    >
      <DebouncedSearchInput
        defaultValue={props.filters.q ?? ""}
        onDebouncedChange={(v) => props.onSearchChange(v ? v : null)}
        testId="mission-list-search"
        ariaLabel={t("missions.index.filters.search_aria")}
        placeholder={t("missions.index.filters.search_placeholder")}
        name="q"
        className="w-full sm:max-w-xs"
      />
      {fields}
      {showClear && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={props.onClearAll}
          data-testid="mission-list-filter-clear"
        >
          {t("missions.index.filters.clear")}
        </Button>
      )}
    </div>
  )
}

/** Returns true if any filter has a non-default value. */
function hasActiveFilter(filters: MissionListFilters): boolean {
  return (
    filters.status !== "active" ||
    filters.mission_type !== "all" ||
    filters.deadline_window !== "all" ||
    (filters.q !== null && filters.q.trim() !== "")
  )
}

interface FilterFieldsProps {
  filters: MissionListFilters
  filterOptions: MissionListFilterOptions
  onStatusChange: (value: MissionListStatus) => void
  onMissionTypeChange: (value: MissionListMissionType) => void
  onDeadlineWindowChange: (value: MissionListDeadlineWindow) => void
}

function FilterFields({
  filters,
  filterOptions,
  onStatusChange,
  onMissionTypeChange,
  onDeadlineWindowChange,
}: FilterFieldsProps) {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-col gap-1">
        <Label
          htmlFor="mission-list-status-select"
          className="text-muted-foreground text-xs"
        >
          {t("missions.index.filters.status_label")}
        </Label>
        <Select
          items={filterOptions.statuses.map((value) => ({
            value,
            label: t(`missions.index.filters.status_options.${value}`),
          }))}
          value={filters.status}
          onValueChange={(v) => v != null && onStatusChange(v)}
          name="status"
        >
          <SelectTrigger
            id="mission-list-status-select"
            data-testid="mission-list-status-select"
            className="w-[160px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {filterOptions.statuses.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`missions.index.filters.status_options.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label
          htmlFor="mission-list-type-select"
          className="text-muted-foreground text-xs"
        >
          {t("missions.index.filters.mission_type_label")}
        </Label>
        <Select
          items={filterOptions.mission_types.map((value) => ({
            value,
            label: t(`missions.index.filters.mission_type_options.${value}`),
          }))}
          value={filters.mission_type}
          onValueChange={(v) => v != null && onMissionTypeChange(v)}
          name="mission_type"
        >
          <SelectTrigger
            id="mission-list-type-select"
            data-testid="mission-list-type-select"
            className="w-[180px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {filterOptions.mission_types.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`missions.index.filters.mission_type_options.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label
          htmlFor="mission-list-deadline-select"
          className="text-muted-foreground text-xs"
        >
          {t("missions.index.filters.deadline_window_label")}
        </Label>
        <Select
          items={filterOptions.deadline_windows.map((value) => ({
            value,
            label: t(`missions.index.filters.deadline_window_options.${value}`),
          }))}
          value={filters.deadline_window}
          onValueChange={(v) => v != null && onDeadlineWindowChange(v)}
          name="deadline_window"
        >
          <SelectTrigger
            id="mission-list-deadline-select"
            data-testid="mission-list-deadline-select"
            className="w-[180px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {filterOptions.deadline_windows.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`missions.index.filters.deadline_window_options.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  )
}
